import mqtt, { MqttClient } from 'mqtt';

import { store } from '../../store';

let client: MqttClient | null = null;

const origin = import.meta.env.VITE_MQTT_ORIGIN ?? '';

const brokerUrl = import.meta.env.VITE_BROKER_URL ?? '';

const mqttUsername = import.meta.env.VITE_MQTT_USERNAME ?? '';

const mqttPassword = import.meta.env.VITE_MQTT_PASSWORD ?? '';

const topicCallbacks: {
  [topic: string]: Set<(topic: string, message: Uint8Array) => void>;
} = {};

const pendingBrokerTopics = new Set<string>();

const subscribedBrokerTopics = new Set<string>();

const TAB_KEY = 'mqttTabId';

const isReload =
  (
    performance.getEntriesByType('navigation')[0] as
      PerformanceNavigationTiming | undefined
  )?.type === 'reload';

const tabId = (() => {
  if (isReload) {
    const saved = sessionStorage.getItem(TAB_KEY);

    if (saved) return saved;
  }

  const id = Math.random().toString(36).slice(2, 8);

  sessionStorage.setItem(TAB_KEY, id);

  return id;
})();

function getClientId(): string {
  const userId = store.getState().profile?.profileData?.id;

  if (!userId) throw new Error('userId not available yet');

  return `${origin}-${userId}-${tabId}`;
}

function ensureBrokerSubscription(fullTopic: string, mqttClient: MqttClient) {
  if (subscribedBrokerTopics.has(fullTopic)) return;

  subscribedBrokerTopics.add(fullTopic);

  mqttClient.subscribe(fullTopic, err => {
    if (err) {
      subscribedBrokerTopics.delete(fullTopic);
    }
  });
}

function flushBrokerSubscriptions(mqttClient: MqttClient) {
  pendingBrokerTopics.forEach(topic => ensureBrokerSubscription(topic, mqttClient));
}

const pendingPublishes: Array<() => void> = [];

function flushPendingPublishes() {
  const queue = pendingPublishes.splice(0, pendingPublishes.length);

  queue.forEach(run => run());
}

function enqueuePublish(run: () => void, mqttClient: MqttClient) {
  if (mqttClient.connected) {
    run();

    return;
  }

  pendingPublishes.push(run);
}

function attachClientHandlers(mqttClient: MqttClient) {
  mqttClient.on('connect', () => {
    subscribedBrokerTopics.clear();

    flushBrokerSubscriptions(mqttClient);

    flushPendingPublishes();
  });

  mqttClient.on('message', (receivedTopic, message) => {
    const originalTopic = receivedTopic.replace(origin, '');

    Object.entries(topicCallbacks).forEach(([subTopic, callbacks]) => {
      const base = subTopic.replace('#', '');

      if (receivedTopic.startsWith(`${origin}${base}`)) {
        callbacks.forEach(cb => cb(originalTopic, message));
      }
    });
  });
}

export function getMqttClient(): MqttClient {
  if (client) return client;

  client = mqtt.connect(brokerUrl, {
    protocolVersion: 5 as const,

    clientId: getClientId(),

    username: mqttUsername,

    password: mqttPassword,

    clean: true,

    reconnectPeriod: 3000,

    connectTimeout: 10_000,
  });

  attachClientHandlers(client);

  return client;
}

export function initMqttClient(): void {
  if (client) return;

  const userId = store.getState().profile?.profileData?.id;

  if (!userId) return;

  getMqttClient();
}

export function subscribe(
  topic: string,

  callback: (topic: string, message: Uint8Array) => void,
) {
  const fullTopic = `${origin}${topic}`;

  const mqttClient = getMqttClient();

  if (!topicCallbacks[topic]) {
    topicCallbacks[topic] = new Set();

    pendingBrokerTopics.add(fullTopic);

    if (mqttClient.connected) {
      flushBrokerSubscriptions(mqttClient);
    }
  }

  topicCallbacks[topic].add(callback);
}

export function unsubscribe(
  topic: string,

  callback: (topic: string, message: Uint8Array) => void,
) {
  if (!topicCallbacks[topic]) return;

  topicCallbacks[topic].delete(callback);

  if (topicCallbacks[topic].size === 0) {
    const fullTopic = `${origin}${topic}`;

    pendingBrokerTopics.delete(fullTopic);

    subscribedBrokerTopics.delete(fullTopic);

    if (client?.connected) {
      client.unsubscribe(fullTopic);
    }

    delete topicCallbacks[topic];
  }
}

export const publish = (
  topic: string,

  message: string,

  callback?: (err?: Error) => void,
) => {
  const fullTopic = `${origin}${topic}`;

  const mqttClient = getMqttClient();

  try {
    const parsed = JSON.parse(message);

    if (parsed?.provisional) {
      sessionStorage.setItem('pendingProvisionalId', parsed.provisional);

      sessionStorage.setItem(
        `createdAt:${parsed.provisional}`,

        new Date().toISOString(),
      );

      sessionStorage.setItem(`messageSent:${parsed.provisional}`, 'true');
    }
  } catch {
    // ignore malformed provisional metadata
  }

  const token = localStorage.getItem('token');

  const options: mqtt.IClientPublishOptions = {
    qos: 1,

    properties: {
      userProperties: {
        token: `Bearer ${token}`,
      },
    },
  };

  const doPublish = () => {
    mqttClient.publish(fullTopic, message, options, err => {
      callback?.(err ?? undefined);
    });
  };

  enqueuePublish(doPublish, mqttClient);
};
